import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  AuthLayout,
  createAccount,
  currentUserEmail,
  refreshIdToken,
  sendEmailVerificationLink,
  signInWithPassword,
} from "../../../auth";
import { acceptPortalInvitation, getPortalInvitation } from "../../application/portalInvitation";
import type { PatientInvitation } from "../../domain/types";

type Stage = "loading" | "form" | "error";

/**
 * How this visitor gets portal access to the invited record.
 *
 *  - `register`: no account yet. Set a password; the account is created and
 *    the record linked to it. This is the first professional's path.
 *  - `link`: already signed in as the invited address. One button, no password.
 *  - `signInAndLink`: the address already has an account but this browser is
 *    not signed in. Ask for the existing password, then link.
 *
 * Every professional after the first arrives at one of the last two. An
 * address that already has an account is the ordinary case here, not an error.
 */
type Mode = "register" | "link" | "signInAndLink";

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stage, setStage] = useState<Stage>("loading");
  const [invitation, setInvitation] = useState<PatientInvitation | null>(null);
  const [errorDetail, setErrorDetail] = useState<string>("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("register");

  useEffect(() => {
    if (!token) {
      setErrorDetail(t("portal.invitation.notFound", "Invalid invitation link."));
      setStage("error");
      return;
    }
    getPortalInvitation(token)
      .then((data) => {
        if (data.state !== "pending") {
          setErrorDetail(
            t("portal.invitation.notConsuamble", "This invitation has already been used or expired.")
          );
          setStage("error");
        } else {
          setInvitation(data);
          // Already signed in as the invited address? Then there is nothing to
          // create and nothing to ask for: just add this professional.
          const current = currentUserEmail()?.trim().toLowerCase();
          if (current && current === data.email.trim().toLowerCase()) {
            setMode("link");
          }
          setStage("form");
        }
      })
      .catch(() => {
        setErrorDetail(t("portal.invitation.notFound", "Invalid invitation link."));
        setStage("error");
      });
  }, [token, t]);

  const finish = async () => {
    if (!token) return;
    await refreshIdToken();
    await acceptPortalInvitation(token);
    // The role claim is set as part of accepting; refresh so this tab has it.
    await refreshIdToken();
    // Which panel to open is settled after the link exists, by the account
    // listing. Sending them to the consent screen for the invited record is
    // the same first step as always.
    navigate(`/portal/consent`, { replace: true });
  };

  const describeError = (err: unknown) => {
    const error = err as {
      code?: string;
      response?: { status?: number; data?: { detail?: string } };
    };
    if (error?.code === "auth/weak-password") {
      return t("portal.invitation.weakPassword", "Password must be at least 6 characters.");
    }
    if (error?.code === "auth/wrong-password" || error?.code === "auth/invalid-credential") {
      return t("portal.invitation.wrongPassword", "That password is not correct.");
    }
    if (error?.response?.data?.detail === "invitation_email_mismatch") {
      // The server only lets the account the invitation was sent to accept it.
      return t(
        "portal.invitation.emailMismatch",
        "This invitation was sent to a different email address."
      );
    }
    if (error?.response?.data?.detail === "email_not_verified") {
      // An account that existed before this invitation has to prove its
      // address first; the verification email is sent on the way here.
      return t(
        "portal.invitation.verifyEmailFirst",
        "Please verify your email address first: we have sent you a link. Open it, then come back and try again."
      );
    }
    if (error?.response?.data?.detail === "record_already_linked") {
      return t(
        "portal.invitation.recordAlreadyLinked",
        "This invitation belongs to another portal account."
      );
    }
    if (error?.response?.status === 409) {
      return t("portal.invitation.alreadyUsed", "This invitation has already been accepted.");
    }
    return t("portal.invitation.genericError", "Something went wrong. Please try again.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation || !token) return;
    setFormError(null);
    setSubmitting(true);

    try {
      if (mode === "register") {
        try {
          await createAccount(invitation.email, password);
        } catch (err: unknown) {
          if ((err as { code?: string })?.code === "auth/email-already-in-use") {
            // Not an error: this address already has a portal account, which
            // is exactly what happens when a second professional invites
            // somebody who is already using the portal. Ask for the password
            // they already have instead of stopping here.
            setMode("signInAndLink");
            setFormError(null);
            setPassword("");
            return;
          }
          throw err;
        }
      } else if (mode === "signInAndLink") {
        await signInWithPassword(invitation.email, password);
      }
      await finish();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (detail === "email_not_verified") {
        // Best effort, and only with a signed-in user to send it to.
        await sendEmailVerificationLink(window.location.href);
      }
      setFormError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === "loading") {
    return (
      <AuthLayout
        panelTagline={t("portal.invitation.panelTagline")}
        panelTitle={t("portal.invitation.panelTitle")}
      >
        <p className="text-gray-500">{t("common.loading", "Loading…")}</p>
      </AuthLayout>
    );
  }

  if (stage === "error") {
    return (
      <AuthLayout
        panelTagline={t("portal.invitation.panelTagline")}
        panelTitle={t("portal.invitation.panelTitle")}
      >
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-content-primary">
            {t("portal.invitation.invalidTitle", "This invitation is no longer valid")}
          </h2>
          <p className="text-sm text-content-tertiary">{errorDetail}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      panelTagline={t("portal.invitation.panelTagline")}
      panelTitle={t("portal.invitation.panelTitle")}
    >
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-content-primary mb-2">
            {mode === "register"
              ? t("portal.invitation.title", "Create your account")
              : t("portal.invitation.addTitle", "Add this professional to your account")}
          </h1>
          <p className="text-content-tertiary">
            {mode === "register" &&
              t("portal.invitation.subtitle", "Set a password to access your therapy portal.")}
            {mode === "link" &&
              t(
                "portal.invitation.addSubtitle",
                "You are signed in. This will add a new panel to the account you already use.",
              )}
            {mode === "signInAndLink" &&
              t(
                "portal.invitation.alreadyHaveAccount",
                "This address already has a portal account. Sign in and we will add this panel to it.",
              )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {formError && (
            <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm">
              {formError}
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-content-secondary">
              {t("portal.invitation.emailLabel", "Email")}
            </label>
            <input
              type="email"
              value={invitation?.email ?? ""}
              disabled
              className="w-full rounded-xl border border-gray-200 bg-gray-100 dark:bg-gray-800 px-4 py-3 text-sm text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Signed in as the invited address already: nothing to type. */}
          {mode !== "link" && (
            <div className="space-y-2">
              <label
                htmlFor="portal-invitation-password"
                className="block text-sm font-medium text-content-secondary"
              >
                {mode === "register"
                  ? t("portal.invitation.passwordLabel", "Password")
                  : t("portal.invitation.existingPasswordLabel", "Your portal password")}
              </label>
              <input
                id="portal-invitation-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder={
                  mode === "register"
                    ? t("portal.invitation.passwordPlaceholder", "At least 6 characters")
                    : t("portal.invitation.existingPasswordPlaceholder", "The one you already use")
                }
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || (mode !== "link" && !password)}
            className="w-full rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-3 transition-colors"
          >
            {submitting
              ? t("portal.invitation.working", "Working…")
              : mode === "register"
                ? t("portal.invitation.submit", "Create account")
                : t("portal.invitation.addThisTherapist", "Add this professional")}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
